import React, { createElement, forwardRef } from 'react';

/**
 * L'échelle typographique du socle.
 *
 * Elle existe pour rendre inutile ce que #34 interdit : les six tailles
 * arbitraires relevées sur la maquette (`text-[13px]` et compagnie) et les gris
 * bruts de Tailwind, dont `text-gray-400` plafonne à 2,54:1 sur blanc — sous le
 * seuil AA, et c'est justement la couleur des onglets de canaux que #16 s'est
 * battu à rendre permanents.
 *
 * Les tons ci-dessous sont mesurés sur `main-bg-color` (#F8F7F6) :
 * `text-color` 11,81:1 · `text-color-light` 5,47:1 · `main-color` 5,65:1 ·
 * `alert-text-color` 6,11:1. Aucun ne descend sous AA.
 */
type Variante = 'titre' | 'sousTitre' | 'corps' | 'petit';
type Ton = 'normal' | 'attenue' | 'marque' | 'danger';

const VARIANTES: Record<Variante, string> = {
    titre: 'text-xl font-semibold',
    sousTitre: 'text-base font-semibold',
    corps: 'text-base',
    petit: 'text-sm',
};

const TONS: Record<Ton, string> = {
    normal: 'text-text-color',
    attenue: 'text-text-color-light',
    marque: 'text-main-color',
    danger: 'text-alert-text-color',
};

interface TextProps {
    children: React.ReactNode;
    variante?: Variante;
    ton?: Ton;
    /** `p` par défaut. Le niveau de titre est un choix de document, pas de style. */
    as?: 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'div';
    className?: string;
}

/*
 * `forwardRef` : la bulle du fil mesure son paragraphe tronqué pour savoir s'il
 * déborde (« Voir plus »). `createElement` plutôt que `<Balise ref={ref}>` : sur
 * l'union de balises, le JSX exige une ref de CHAQUE type d'élément à la fois
 * (TS2322) ; `createElement` l'infère en `HTMLElement`, sans cast.
 */
const Text = forwardRef<HTMLElement, TextProps>(function Text(
    { children, variante = 'corps', ton = 'normal', as: Balise = 'p', className = '' },
    ref,
) {
    return createElement(
        Balise,
        { ref, className: `${VARIANTES[variante]} ${TONS[ton]} ${className}`.trim() },
        children,
    );
});

export default Text;
